import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as inv from "./api";

// --- vendors ---

export function useVendors(filters: object, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["vendors", filters],
    queryFn: () => inv.listVendors(filters),
    placeholderData: (p) => p,
    enabled: options.enabled ?? true,
  });
}

function useInvalidate(keys: string[]) {
  const qc = useQueryClient();
  return () => keys.forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));
}

export function useCreateVendor() {
  const inv2 = useInvalidate(["vendors"]);
  return useMutation({ mutationFn: inv.createVendor, onSuccess: inv2 });
}
export function useUpdateVendor() {
  const inv2 = useInvalidate(["vendors"]);
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<inv.VendorBody> }) =>
      inv.updateVendor(id, body),
    onSuccess: inv2,
  });
}
export function useDeleteVendor() {
  const inv2 = useInvalidate(["vendors", "products"]);
  return useMutation({ mutationFn: inv.deleteVendor, onSuccess: inv2 });
}

// --- products ---

export function useProducts(filters: object, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["products", filters],
    queryFn: () => inv.listProducts(filters),
    placeholderData: (p) => p,
    enabled: options.enabled ?? true,
  });
}

export function useCreateProduct() {
  const inv2 = useInvalidate(["products"]);
  return useMutation({ mutationFn: inv.createProduct, onSuccess: inv2 });
}
export function useUpdateProduct() {
  const inv2 = useInvalidate(["products"]);
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<inv.ProductBody> }) =>
      inv.updateProduct(id, body),
    onSuccess: inv2,
  });
}
export function useDeleteProduct() {
  const inv2 = useInvalidate(["products"]);
  return useMutation({ mutationFn: inv.deleteProduct, onSuccess: inv2 });
}
export function useAdjustStock() {
  const inv2 = useInvalidate(["products"]);
  return useMutation({
    mutationFn: ({ id, quantity, note }: { id: string; quantity: number; note?: string | undefined }) =>
      inv.adjustStock(id, quantity, note),
    onSuccess: inv2,
  });
}

// --- purchase orders ---

export function usePurchaseOrders(filters: object, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["purchase-orders", filters],
    queryFn: () => inv.listPos(filters),
    placeholderData: (p) => p,
    enabled: options.enabled ?? true,
  });
}

// PO mutations also touch products (receiving moves stock).
export function useCreatePo() {
  const inv2 = useInvalidate(["purchase-orders"]);
  return useMutation({ mutationFn: inv.createPo, onSuccess: inv2 });
}
export function useSubmitPo() {
  const inv2 = useInvalidate(["purchase-orders"]);
  return useMutation({ mutationFn: inv.submitPo, onSuccess: inv2 });
}
export function useReceivePo() {
  const inv2 = useInvalidate(["purchase-orders", "products"]);
  return useMutation({
    mutationFn: ({ id, lines }: { id: string; lines?: { product_id: string; quantity: number }[] }) =>
      inv.receivePo(id, lines),
    onSuccess: inv2,
  });
}
export function useCancelPo() {
  const inv2 = useInvalidate(["purchase-orders"]);
  return useMutation({ mutationFn: inv.cancelPo, onSuccess: inv2 });
}

// --- RMAs ---

export function useRmas(filters: object, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["rmas", filters],
    queryFn: () => inv.listRmas(filters),
    placeholderData: (p) => p,
    enabled: options.enabled ?? true,
  });
}

export function useCreateRma() {
  const inv2 = useInvalidate(["rmas"]);
  return useMutation({ mutationFn: inv.createRma, onSuccess: inv2 });
}
export function useUpdateRma() {
  const inv2 = useInvalidate(["rmas"]);
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof inv.updateRma>[1] }) =>
      inv.updateRma(id, body),
    onSuccess: inv2,
  });
}
